import { Injectable } from '@angular/core';
import {
  InverterData,
  AutoTestState,
  AutoTestKeys,
  AutoTestStateStyles,
} from 'ngx-modbus';

export interface AutoTestVoltageResult {
  name: string;
  voltageValue: number;
  timeValue: number;
  voltageDefault: number;
  timeDefault: number;
}

export interface AutoTestFrequencyResult {
  name: string;
  frequencyValue: number;
  timeValue: number;
  frequencyDefault: number;
  timeDefault: number;
}

export interface AutoTestParsedData {
  state: number | null;
  voltageResults: AutoTestVoltageResult[];
  frequencyResults: AutoTestFrequencyResult[];
}

const THRESHOLD_VOLTAGE: Record<number, string> = { 1: '59.S1', 2: '59.S2', 3: '27.S1', 4: '27.S2' };
const THRESHOLD_FREQUENCY: Record<number, string> = { 1: '81>S1', 2: '81>S2', 3: '81<S1', 4: '81<S2' };

@Injectable({ providedIn: 'root' })
export class AutoTestService {

  /** Parse raw modbus data into structured auto-test results */
  parseData(data: Partial<InverterData>): AutoTestParsedData {
    const raw = data as Record<string, any>;
    const state: number | null = raw.AutoTest_control ?? null;

    const voltageResults: AutoTestVoltageResult[] = [];
    for (let i = 1; i <= 4; i++) {
      const v = raw[`AUTO_TEST_RESULT_V_${i}`];
      const ms = raw[`AUTO_TEST_RESULT_V_MS_${i}`];
      if (v !== undefined && ms !== undefined) {
        voltageResults.push({
          name: THRESHOLD_VOLTAGE[i],
          voltageValue: v,
          timeValue: ms,
          voltageDefault: raw[`AUTO_TEST_RESULT_V_DEFAULT_${i}`],
          timeDefault: raw[`AUTO_TEST_RESULT_V_MS_DEFAULT_${i}`],
        });
      }
    }

    const frequencyResults: AutoTestFrequencyResult[] = [];
    for (let i = 1; i <= 4; i++) {
      const hz = raw[`AUTO_TEST_RESULT_HZ_${i}`];
      const ms = raw[`AUTO_TEST_RESULT_HZ_MS_${i}`];
      if (hz !== undefined && ms !== undefined) {
        frequencyResults.push({
          name: THRESHOLD_FREQUENCY[i],
          frequencyValue: hz,
          timeValue: ms,
          frequencyDefault: raw[`AUTO_TEST_RESULT_HZ_DEFAULT_${i}`],
          timeDefault: raw[`AUTO_TEST_RESULT_HZ_MS_DEFAULT_${i}`],
        });
      }
    }

    return { state, voltageResults, frequencyResults };
  }

  /** Map auto-test state enum to an i18n key (consumer must translate) */
  getStateLabelKey(state: number | null): string {
    if (state === null) {
      return '';
    }
    return AutoTestKeys[state as AutoTestState] ?? `${state}`; // i18n-allowed
  }

  /** Map auto-test state enum to a CSS background color variable value */
  getStateColor(state: number | null): string {
    if (state === null) {
      return '';
    }
    const style = AutoTestStateStyles[state as AutoTestState];
    return style ? style['--background'] || '' : '';
  }
}
